import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Wrench, FileText, AlertTriangle, Database, TrendingUp, Clock, Play } from 'lucide-react';
import { AlertasPanel, RespaldosPanel, TarifasInactivasCard, formatBytes, formatDate } from '../components/mantenimiento/MantenimientoPaneles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const formatMoney = (value, moneda = 'COP') =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: moneda, maximumFractionDigits: 0 }).format(value || 0);

const SummaryCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${accent}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-800 truncate">{value}</p>
      </div>
      <Icon className="w-9 h-9 text-slate-300 flex-shrink-0" />
    </div>
  </div>
);

const DiasBadge = ({ dias }) => {
  const color = dias <= 7 ? 'bg-red-100 text-red-700' : dias <= 15 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${color}`}>{dias === 0 ? 'Hoy' : `${dias} ${dias === 1 ? 'día' : 'días'}`}</span>;
};

const CotizacionesTable = ({ items, onOpen }) => (
  <section className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="mantenimiento-cotizaciones">
    <div className="px-5 py-4 border-b bg-amber-50">
      <h2 className="font-semibold text-amber-900 flex items-center gap-2"><FileText className="w-5 h-5" />Cotizaciones de más de 45 días sin clasificar</h2>
      <p className="text-xs text-amber-800/80 mt-1">Sin venta, ficha ni abono. Se borran solas al cumplir 60 días: apruébalas, recházalas o conviértelas en venta para decidir su destino.</p>
    </div>
    {items.length === 0 ? (
      <p className="p-8 text-center text-slate-500" data-testid="mantenimiento-sin-cotizaciones">Todo en orden: no hay cotizaciones pendientes de revisar.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>{['Número', 'Cliente', 'Vendedor', 'Estado', 'Total', 'Creada', 'Se borra en'].map(h => <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {items.map(q => (
              <tr key={q.id} onClick={() => onOpen(q.id)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`mantenimiento-cotizacion-${q.numero}`}>
                <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{q.numero}</td>
                <td className="px-4 py-3 text-slate-700">{q.cliente_nombre}</td>
                <td className="px-4 py-3 text-slate-600">{q.vendedor_nombre || '-'}</td>
                <td className="px-4 py-3 capitalize text-slate-600">{q.estado}</td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatMoney(q.total, q.moneda)}</td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(q.created_at)} · {q.dias_antiguedad} d</td>
                <td className="px-4 py-3"><DiasBadge dias={q.dias_para_borrado} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const ColeccionesList = ({ colecciones, total }) => (
  <section className="bg-white rounded-xl shadow-sm p-5" data-testid="mantenimiento-colecciones">
    <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><Database className="w-5 h-5 text-indigo-500" />Lo que más ocupa</h2>
    <ul className="space-y-3">
      {colecciones.map(c => (
        <li key={c.nombre}>
          <div className="flex justify-between text-sm gap-2">
            <span className="text-slate-700 truncate">{c.nombre} <span className="text-slate-400">· {c.documentos.toLocaleString('es-CO')} reg.</span></span>
            <span className="text-slate-500 whitespace-nowrap">{formatBytes(c.tamano_bytes)}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${Math.max(2, (c.tamano_bytes / (total || 1)) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  </section>
);

const HistorialList = ({ historial }) => (
  <section className="bg-white rounded-xl shadow-sm p-5" data-testid="mantenimiento-historial">
    <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-slate-500" />Informes anteriores</h2>
    <ul className="divide-y text-sm">
      {historial.map(h => (
        <li key={h.id} className="py-2 flex justify-between gap-2">
          <span className="text-slate-700">{formatDate(h.generado_en, true)} <span className="text-xs text-slate-400">{h.origen === 'manual' ? '· manual' : '· automático'}</span></span>
          <span className="text-slate-500 whitespace-nowrap">{h.total_por_revisar} por revisar · {formatBytes(h.base_datos?.tamano_datos_bytes)}</span>
        </li>
      ))}
    </ul>
  </section>
);

export default function MantenimientoSemanal() {
  const navigate = useNavigate();
  const [data, setData] = useState({ ultimo: null, historial: [], respaldos: [] });
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [accion, setAccion] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/mantenimiento/informes`, authHeaders());
      setData(res.data);
    } catch (e) {
      toast.error('No se pudo cargar el informe de mantenimiento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const generar = async (silencioso = false) => {
    setGenerando(true);
    try {
      await axios.post(`${API_URL}/api/mantenimiento/informes/generar`, {}, authHeaders());
      if (!silencioso) toast.success('Informe generado');
      await cargar();
    } catch (e) {
      toast.error('No se pudo generar el informe');
    } finally {
      setGenerando(false);
    }
  };

  const ejecutar = async (clave, url, mensajeOk) => {
    setAccion(clave);
    try {
      const res = await axios.post(`${API_URL}${url}`, {}, authHeaders());
      toast.success(mensajeOk(res.data));
      await generar(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar la acción');
    } finally {
      setAccion(null);
    }
  };

  const respaldar = () => ejecutar('respaldo', '/api/mantenimiento/respaldos/crear', (d) => `Respaldo verificado (${formatBytes(d.bytes)})`);
  const limpiarTarifas = () => ejecutar('tarifas', '/api/mantenimiento/tarifas-inactivas/limpiar', (d) => `${d.eliminadas} tarifas desactivadas borradas`);

  const u = data.ultimo;
  const bd = u?.base_datos;
  const crecimiento = u?.crecimiento_bytes;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mantenimiento-page">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg transition" data-testid="mantenimiento-volver"><ArrowLeft className="w-5 h-5" /></button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Wrench className="w-6 h-6 text-indigo-500" />Mantenimiento semanal</h1>
              <p className="text-sm text-slate-500">Se genera solo cada domingo a las 9:00 p. m.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cargar} className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition" data-testid="mantenimiento-actualizar">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">Actualizar</span>
            </button>
            <button onClick={() => generar()} disabled={generando} className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition disabled:opacity-60" data-testid="mantenimiento-generar">
              <Play className={`w-4 h-4 ${generando ? 'animate-pulse' : ''}`} />{generando ? 'Generando…' : 'Generar ahora'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {!loading && !u && (
          <div className="bg-white rounded-xl shadow-sm p-10 text-center text-slate-500" data-testid="mantenimiento-vacio">
            Aún no hay informes. El primero se generará el domingo, o pulsa “Generar ahora”.
          </div>
        )}
        {u && (
          <>
            <p className="text-sm text-slate-500" data-testid="mantenimiento-fecha">Último informe: {formatDate(u.generado_en, true)} ({u.origen === 'manual' ? 'manual' : 'automático'})</p>
            <AlertasPanel alertas={u.alertas} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard icon={FileText} label="Por revisar (+45 días)" value={u.total_por_revisar} accent="border-amber-400" testId="mantenimiento-total-revisar" />
              <SummaryCard icon={AlertTriangle} label="Se borran en 7 días o menos" value={u.proximas_a_borrarse} accent="border-red-400" testId="mantenimiento-proximas-borrar" />
              <SummaryCard icon={Database} label="Tamaño de la base de datos" value={formatBytes(bd.tamano_datos_bytes)} accent="border-indigo-400" testId="mantenimiento-tamano-bd" />
              <SummaryCard icon={TrendingUp} label="Crecimiento desde el anterior" value={crecimiento === null ? '—' : `${crecimiento >= 0 ? '+' : ''}${formatBytes(crecimiento)}`} accent="border-emerald-400" testId="mantenimiento-crecimiento" />
            </div>
            <CotizacionesTable items={u.cotizaciones_por_revisar} onOpen={(id) => navigate(`/quotes/${id}`)} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RespaldosPanel respaldos={data.respaldos} onCrear={respaldar} creando={accion === 'respaldo'} />
              <TarifasInactivasCard tarifas={u.tarifas_inactivas} onLimpiar={limpiarTarifas} limpiando={accion === 'tarifas'} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ColeccionesList colecciones={bd.colecciones} total={bd.colecciones[0]?.tamano_bytes} />
              <HistorialList historial={data.historial} />
            </div>
            <p className="text-xs text-slate-400" data-testid="mantenimiento-resumen-bd">{bd.total_documentos.toLocaleString('es-CO')} registros en {bd.total_colecciones} colecciones · almacenamiento en disco {formatBytes(bd.tamano_almacenamiento_bytes)} · índices {formatBytes(bd.tamano_indices_bytes)}</p>
          </>
        )}
      </main>
    </div>
  );
}
